import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  hospitalAdminApi,
  type GoogleEventQueueRow,
  type GoogleEventQueueStatus,
} from "@/api/hospitalAdmin";
import { toast } from "sonner";

function statusClass(status: GoogleEventQueueStatus) {
  switch (status) {
    case "SENT":
      return "text-green-700";
    case "FAILED":
    case "DEAD":
      return "text-red-700";
    default:
      return "text-amber-700";
  }
}

function displayStatus(row: GoogleEventQueueRow): { label: string; className: string; hint?: string } {
  const req = row.logs?.[0]?.request as { validateOnly?: boolean; test?: boolean } | undefined;
  const validateOnly = req?.validateOnly === true || (req?.test === true && !row.leadId);
  if (row.status === "SENT" && validateOnly) {
    return {
      label: "VALIDATED",
      className: "text-blue-700",
      hint: "Credentials check only — not recorded in Google Ads",
    };
  }
  return { label: row.status, className: statusClass(row.status) };
}

export default function GoogleConversionLogsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<GoogleEventQueueStatus | "ALL">("ALL");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["google-event-logs", page, status],
    queryFn: () =>
      hospitalAdminApi.listGoogleConversionLogs({
        page,
        limit: 25,
        ...(status !== "ALL" ? { status } : {}),
      }),
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => hospitalAdminApi.retryGoogleConversionEvent(id),
    onSuccess: (row) => {
      const ok = row?.status === "SENT";
      toast[ok ? "success" : "error"](
        ok ? "Conversion sent to Google" : row?.lastError || "Upload failed — see Audit for details"
      );
      qc.invalidateQueries({ queryKey: ["google-event-logs"] });
    },
    onError: (e: Error) => toast.error(e.message || "Send failed"),
  });

  const processPendingMutation = useMutation({
    mutationFn: () => hospitalAdminApi.processPendingGoogleConversionEvents(),
    onSuccess: (data) => {
      toast.success(`Processed ${data?.processed ?? 0} event(s); ${data?.remaining ?? 0} still pending`);
      qc.invalidateQueries({ queryKey: ["google-event-logs"] });
    },
    onError: (e: Error) => toast.error(e.message || "Failed to process pending events"),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));
  const pendingCount = rows.filter((r) => r.status === "PENDING").length;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Google Event Logs</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Audit trail for outbound Google Ads offline conversion uploads.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>Events</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {(status === "PENDING" || status === "ALL") && pendingCount > 0 ? (
              <Button
                size="sm"
                variant="default"
                disabled={processPendingMutation.isPending}
                onClick={() => processPendingMutation.mutate()}
              >
                Send all pending ({pendingCount} on page)
              </Button>
            ) : null}
            <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v as typeof status);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="SENT">Sent</SelectItem>
              <SelectItem value="FAILED">Failed</SelectItem>
              <SelectItem value="DEAD">Dead</SelectItem>
            </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Lead</th>
                    <th className="py-2 pr-3">Event</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Value</th>
                    <th className="py-2 pr-3">Attempts</th>
                    <th className="py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: GoogleEventQueueRow) => {
                    const statusInfo = displayStatus(row);
                    return (
                    <Fragment key={row.id}>
                      <tr className="border-b align-top">
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {new Date(row.createdAt).toLocaleString()}
                        </td>
                        <td className="py-2 pr-3">
                          {row.lead ? (
                            <>
                              <div className="font-medium">{row.lead.patientName}</div>
                              <div className="text-xs text-muted-foreground">{row.lead.phone}</div>
                            </>
                          ) : row.leadId ? (
                            <span className="text-xs text-muted-foreground" title={row.leadId}>
                              Lead ID: {row.leadId.slice(0, 8)}…
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="py-2 pr-3">{row.eventName}</td>
                        <td className={`py-2 pr-3 font-medium ${statusInfo.className}`}>
                          {statusInfo.label}
                          {statusInfo.hint ? (
                            <div className="text-xs font-normal text-muted-foreground max-w-xs">
                              {statusInfo.hint}
                            </div>
                          ) : null}
                          {row.lastError ? (
                            <div
                              className="text-xs font-normal text-muted-foreground max-w-xs truncate"
                              title={row.lastError}
                            >
                              {row.lastError}
                            </div>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3">
                          {row.conversionValue != null ? `₹${Number(row.conversionValue).toLocaleString("en-IN")}` : "—"}
                        </td>
                        <td className="py-2 pr-3">{row.attempts}</td>
                        <td className="py-2 space-x-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                          >
                            {expandedId === row.id ? "Hide" : "Audit"}
                          </Button>
                          {(row.status === "FAILED" || row.status === "DEAD" || row.status === "PENDING") && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={retryMutation.isPending}
                              onClick={() => retryMutation.mutate(row.id)}
                            >
                              {row.status === "PENDING" ? "Send" : "Retry"}
                            </Button>
                          )}
                        </td>
                      </tr>
                      {expandedId === row.id ? (
                        <tr className="border-b bg-muted/30">
                          <td colSpan={7} className="py-3 px-2">
                            <div className="grid gap-3 md:grid-cols-2 text-xs">
                              <div>
                                <div className="font-medium mb-1">
                                  {row.logs?.[0] ? "Request payload" : "Queued payload (not sent yet)"}
                                </div>
                                <pre className="whitespace-pre-wrap break-words bg-background border rounded p-2 max-h-72 overflow-auto">
                                  {JSON.stringify(
                                    row.logs?.[0]?.request ?? row.payload ?? {},
                                    null,
                                    2
                                  )}
                                </pre>
                              </div>
                              <div>
                                <div className="font-medium mb-1">Response</div>
                                <pre className="whitespace-pre-wrap break-words bg-background border rounded p-2 max-h-72 overflow-auto">
                                  {row.logs?.[0]
                                    ? JSON.stringify(
                                        row.logs[0].response ?? row.logs[0].error ?? {},
                                        null,
                                        2
                                      )
                                    : row.lastError
                                      ? JSON.stringify({ error: row.lastError }, null, 2)
                                      : "No upload attempt yet — click Send to upload to Google."}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Page {page} of {totalPages} ({total} events)
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

